import { useCallback, useState } from "react";
import {
  Form,
  Search,
  Label,
  Icon,
  Message,
  type SearchProps,
} from "semantic-ui-react";
import { debounce } from "lodash";
import axios from "../../../apis/dvdlocator";
import "./index.css";

export interface Family {
  id: number;
  title: string;
}

interface FamilySearchFieldProps {
  families: Family[];
  onChange: (families: Family[]) => void;
}

/** Families picker: searches GET /families?name= and shows the picked
 *  families as removable labels. Extracted from ProductFormPage. */
export function FamilySearchField({
  families,
  onChange,
}: FamilySearchFieldProps) {
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState<Family[]>([]);
  const [searchError, setSearchError] = useState("");

  const debouncedSearch = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setSearchResults([]);
        setSearchError("");
        return;
      }
      setSearchLoading(true);
      setSearchError("");
      try {
        const result = await axios.get(`/families?name=${value}`);
        setSearchResults(
          result.data.map((e: any) => ({ id: e.id, title: e.family }))
        );
      } catch (err: any) {
        setSearchError(
          err.response?.data?.message ||
            "An error occurred while searching. Please try again."
        );
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500),
    []
  );

  const handleSearchChange = (_: any, data: SearchProps) => {
    const value = data.value ?? "";
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleResultSelect = (_: any, { result }: any) => {
    if (families.some((f) => f.title === result.title)) return;
    onChange([...families, result]);
    setSearchValue("");
  };

  const handleRemoveFamily = (familyId: number) => {
    onChange(families.filter((f) => f.id !== familyId));
  };

  const customResultRenderer = (result: Family) => {
    const isSelected = families.some((f) => f.title === result.title);
    return (
      <div
        style={{
          opacity: isSelected ? 0.5 : 1,
          cursor: isSelected ? "not-allowed" : "pointer",
          padding: "8px",
          color: isSelected ? "#666" : "inherit",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{result.title}</span>
          {isSelected && (
            <span style={{ fontSize: "0.8em", color: "#666" }}>
              (Already Added)
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Form.Field>
      <label>Families</label>
      <Search
        fluid
        input={{ fluid: true }}
        loading={searchLoading}
        placeholder="Search Families"
        onResultSelect={handleResultSelect}
        onSearchChange={handleSearchChange}
        results={searchResults}
        resultRenderer={(result: any) => customResultRenderer(result)}
        minCharacters={2}
        value={searchValue}
        className="scrolling-search"
      />
      {searchError && (
        <Message error content={searchError} style={{ marginTop: "5px" }} />
      )}
      <div style={{ marginTop: "10px" }}>
        {families.map((family) => (
          <Label
            key={family.id}
            style={{ marginRight: "5px", marginBottom: "5px" }}
          >
            {family.title}
            <Icon name="delete" onClick={() => handleRemoveFamily(family.id)} />
          </Label>
        ))}
      </div>
    </Form.Field>
  );
}
